/**
 * Google Tag Manager web-container bootstrap helper.
 *
 * Rules (see task brief):
 *  - Load exactly once per window (guarded via `window.__truweGtmLoaded`).
 *  - Load only on production hosts (`truwe.in`, `www.truwe.in`).
 *  - Fail closed on localhost, *.lovable.app, admin.truwe.in, and all others.
 *  - Never load on excluded paths (/admin, /auth, /account, /api) so those
 *    sessions stay out of GA4 acquisition reporting entirely.
 *  - Preserve any existing `window.dataLayer`.
 *  - No Meta CAPI / server-container config in the browser.
 */

import { GA_EXCLUDED_PATH_PREFIXES, isExcludedPath } from "./exclusions";

export const GTM_WEB_CONTAINER_ID = "GTM-MHFPF2WP";

export const PRODUCTION_HOSTS = ["truwe.in", "www.truwe.in"] as const;

export function shouldLoadGtm(
  hostname: string | undefined | null,
  pathname?: string | undefined | null,
): boolean {
  if (!hostname) return false;
  const host = hostname.toLowerCase().trim();
  if (!(PRODUCTION_HOSTS as readonly string[]).includes(host)) return false;
  // Logged-in / transactional / machine paths never produce analytics traffic.
  if (pathname !== undefined && isExcludedPath(pathname)) return false;
  return true;
}

/**
 * Returns the inline bootstrap snippet. Safe to inject in <head> — the snippet
 * itself performs the host allowlist check + duplicate-load guard at runtime,
 * so it is a no-op on preview / localhost / admin / any non-prod host.
 */
export function buildGtmSnippet(
  containerId: string = GTM_WEB_CONTAINER_ID,
  productionHosts: readonly string[] = PRODUCTION_HOSTS,
  /** Kept for call-site compatibility; a false value no longer bypasses consent. */
  _legacyRequireConsent: boolean = true,
): string {
  const hosts = JSON.stringify(productionHosts);
  const id = JSON.stringify(containerId);
  const excluded = JSON.stringify(GA_EXCLUDED_PATH_PREFIXES);
  // Conservative mixed-container gate. Single-category consent still works
  // for first-party analytics but does not start this combined vendor container.
  return `(function(){try{
var w=window,d=document,hosts=${hosts},excluded=${excluded};
function eligible(){
 var h=(location.hostname||'').toLowerCase(),p=(location.pathname||'/').toLowerCase();
 if(hosts.indexOf(h)===-1)return false;
 for(var j=0;j<excluded.length;j++){var e=excluded[j];if(p===e||p.indexOf(e+'/')===0)return false;}
 try{var c=JSON.parse(w.localStorage.getItem('truwe_privacy_consent_v1')||'null');return !!c&&c.source!=='migrated_cookie_choice'&&c.analytics==='granted'&&c.advertising==='granted';}catch(e){return false;}
}
if(hosts.indexOf((location.hostname||'').toLowerCase())===-1)return;
if(w.__truweGtmLoaded)return;
w.__truweGtmLoaded=true;w.dataLayer=w.dataLayer||[];
function boot(){
 if(w.__truweGtmBooted||!eligible())return;
 w.__truweGtmBooted=true;
 w.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});
 var s=d.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtm.js?id='+${id};
 (d.head||d.documentElement).appendChild(s);
}
w.__truweBootGtm=boot;
// Withdrawal requires a new document because third-party scripts cannot be
// reliably unloaded. Save the choice first; normal local cart storage survives.
function changed(){
 if(w.__truweGtmBooted&&!eligible()){
  if(!w.__truweConsentReloadPending){w.__truweConsentReloadPending=true;setTimeout(function(){location.reload();},750);}
  return;
 }
 if(eligible())boot();
}
w.addEventListener('truwe:privacy-consent-change',changed);
w.addEventListener('storage',function(e){if(e.key===null||e.key==='truwe_privacy_consent_v1')changed();});
var evts=['pointerdown','keydown','touchstart','scroll'];
function fire(){for(var k=0;k<evts.length;k++)w.removeEventListener(evts[k],fire,true);boot();}
for(var k=0;k<evts.length;k++)w.addEventListener(evts[k],fire,{once:true,passive:true,capture:true});
if(w.requestIdleCallback)w.requestIdleCallback(boot,{timeout:4000});else setTimeout(boot,2500);
setTimeout(boot,8000);
}catch(e){}})();`;
}
